<template>
  <div class="space-y-6">
    <div
      v-if="title || description || $slots.title || $slots.actions"
      class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
    >
      <div v-if="title || description || $slots.title">
        <slot name="title">
          <h3 v-if="title" class="text-lg font-medium leading-6 text-gray-900">{{ title }}</h3>
        </slot>
        <p v-if="description" class="max-w-4xl mt-2 text-sm text-gray-500">
          {{ description }}
        </p>
      </div>
      <div v-if="$slots.actions" class="flex flex-wrap shrink-0 gap-2">
        <slot name="actions" />
      </div>
    </div>
    <SimpleAlert v-if="infoAlertDescription" :description="infoAlertDescription" />
    <SimpleAlert v-if="$slots.info">
      <slot name="info" />
    </SimpleAlert>
    <slot name="filters" />
    <slot v-if="showEmptyState" name="empty-state" />
    <div v-else-if="$slots.body || $slots.draggableBody || showGhost" class="flex flex-col">
      <div
        class="overflow-x-auto"
        :class="{
          '-mx-4 sm:-mx-6': !isContained,
        }"
      >
        <div
          class="inline-block min-w-full align-middle"
          :class="{
            'px-4 sm:px-6': !isContained,
          }"
        >
          <div
            class="overflow-hidden"
            :class="{
              'border border-gray-200': !noBorder,
              'rounded shadow': asCard,
              rounded: isContained,
            }"
          >
            <table
              class="min-w-full divide-y divide-gray-200"
              :class="{
                'table-fixed w-full': isContained,
              }"
            >
              <thead class="bg-gray-50">
                <slot name="header" />
              </thead>
              <tbody v-if="showGhost" class="bg-white divide-y divide-gray-200">
                <tr v-for="(row, index) in new Array(ghostRowsCount)" :key="index">
                  <td
                    v-for="(column, index) in new Array(ghostColumnsCount)"
                    :key="index"
                    class="px-6 py-4"
                  >
                    <Box
                      class="animate-pulse"
                      background-color="secondary"
                      paddingX="gutter"
                      paddingY="small"
                      border-radius="base"
                    />
                  </td>
                </tr>
              </tbody>
              <Draggable
                :handle="draggableHandle"
                :itemKey="draggableItemKey"
                class="bg-white divide-y divide-gray-200"
                tag="tbody"
                v-bind="dragOptions"
                v-else-if="$slots.draggableBody"
                v-model="availableDraggableItems"
                @end="onDragStop"
              >
                <!--
                  NOTE: <Draggable /> requires that elements passed into it's default slot have a single child node,
                  which is why we are wrapping the slot here in a `<tr>`
                -->
                <template #item="{ element }">
                  <tr>
                    <slot :element="element" name="draggableBody" />
                  </tr>
                </template>
              </Draggable>
              <tbody v-else class="bg-white divide-y divide-gray-200">
                <slot name="body" />
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <slot name="pagination" />
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { Box } from '@aryeo/runway'
import Draggable from 'vuedraggable'
import SimpleAlert from '@/Runway/Alerts/Simple.vue'

const props = defineProps({
  asCard: {
    type: Boolean,
    default: true,
  },
  description: {
    type: String,
    default: null,
  },
  draggableItems: {
    type: Array,
    default: () => [],
  },
  draggableHandle: {
    type: String,
    default: undefined,
  },
  draggableItemKey: {
    type: String,
    default: 'id',
  },
  ghostColumnsCount: {
    type: Number,
    default: 5,
  },
  ghostRowsCount: {
    type: Number,
    default: 8,
  },
  infoAlertDescription: {
    type: String,
    required: false,
  },
  showEmptyState: {
    type: Boolean,
    default: false,
  },
  showGhost: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: null,
  },
  isContained: {
    type: Boolean,
    default: false,
  },
  noBorder: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['onRearrange'])

const availableDraggableItems = ref(props.draggableItems)

const dragOptions = {
  animation: 200,
  group: 'description',
  disabled: false,
  ghostClass: 'ghost',
}

const onDragStop = () => {
  emit('onRearrange', availableDraggableItems)
}
</script>

<style scoped>
.ghost {
  opacity: 0.5;
  background: #f4f4f5;
}
</style>
